import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Grow,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import { formatAcquisitionMetric } from "../../utils/acquisitionFunnel";

// BLOCK 2 · Homepage becomes an interactive product menu instead of a long explanation.
// STATUS: STATIC_PASS. Runtime/responsive verification pending staging deploy.
const BRAND = { orange: "#F35A3A", orangeSoft: "#FFB19A", ink: "#F8FAFC", panel: "#111820" };

const QUICK_NEEDS = [
  { key: "website", label: "Quiero una web", helper: "Verme profesional y compartir mi negocio", icon: LanguageRoundedIcon },
  { key: "sell", label: "Quiero vender más", helper: "Productos, servicios y un camino comercial", icon: PointOfSaleRoundedIcon },
  { key: "appointments", label: "Quiero ordenar turnos", helper: "Agenda, disponibilidad y menos choques", icon: EventAvailableRoundedIcon },
  { key: "stock", label: "Quiero ordenar stock", helper: "Saber qué tengo y qué se mueve", icon: Inventory2OutlinedIcon },
  { key: "cash", label: "Quiero entender mi plata", helper: "Caja, márgenes y control del negocio", icon: PaymentsRoundedIcon },
  { key: "customers", label: "Quiero ordenar clientes", helper: "Seguimiento y relación comercial", icon: Groups2RoundedIcon },
];

function RealStats({ stats = [] }) {
  if (!stats.length) return null;
  return (
    <Box sx={{ mt: { xs: 2.5, sm: 3.2 }, display: "grid", gridTemplateColumns: { xs: "repeat(3, minmax(0, 1fr))" }, gap: { xs: .7, sm: 1 } }}>
      {stats.slice(0, 3).map((item) => (
        <Box key={item.key} sx={{ px: { xs: .8, sm: 1.2 }, py: { xs: 1, sm: 1.25 }, borderRadius: 2.8, border: "1px solid rgba(255,255,255,.07)", bgcolor: "rgba(255,255,255,.035)", minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950, fontSize: { xs: "1rem", sm: "1.28rem" }, lineHeight: 1.05, color: "#fff" }}>
            {formatAcquisitionMetric(item.value)}
          </Typography>
          <Typography sx={{ mt: .25, color: "rgba(255,255,255,.48)", fontWeight: 750, fontSize: { xs: ".64rem", sm: ".73rem" }, lineHeight: 1.2 }}>
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function HeroSection({
  onStartClick,
  onExploreClick,
  onQuickNeed,
  stats = [],
  primaryLabel = "Empezar",
  isAuthenticated = false,
  userName = "",
}) {
  return (
    <Box
      component="section"
      aria-labelledby="webprofit-home-title"
      sx={{
        position: "relative",
        overflow: "hidden",
        minHeight: { xs: "auto", md: "min(900px, 100vh)" },
        display: "flex",
        alignItems: "center",
        pt: { xs: 11, sm: 13, md: 14 },
        pb: { xs: 6, sm: 8, md: 9 },
        color: BRAND.ink,
        bgcolor: "#0B1016",
        background: "radial-gradient(circle at 82% 18%, rgba(243,90,58,.19), transparent 28%), radial-gradient(circle at 12% 78%, rgba(255,126,87,.08), transparent 30%), linear-gradient(180deg, #090E13 0%, #0B1118 100%)",
        "@keyframes homeGlowFloat": {
          "0%, 100%": { transform: "translate3d(0,0,0) scale(1)" },
          "50%": { transform: "translate3d(-26px,20px,0) scale(1.06)" },
        },
      }}
    >
      <Box aria-hidden sx={{ position: "absolute", width: { xs: 360, md: 620 }, height: { xs: 360, md: 620 }, right: { xs: -230, md: -180 }, top: { xs: 40, md: -30 }, borderRadius: "50%", background: "radial-gradient(circle, rgba(243,90,58,.30), rgba(243,90,58,0) 68%)", filter: "blur(12px)", animation: "homeGlowFloat 9s ease-in-out infinite", pointerEvents: "none", "@media (prefers-reduced-motion: reduce)": { animation: "none" } }} />
      <Box aria-hidden sx={{ position: "absolute", inset: 0, opacity: .2, backgroundImage: "linear-gradient(rgba(255,255,255,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.045) 1px, transparent 1px)", backgroundSize: "46px 46px", maskImage: "linear-gradient(to bottom, rgba(0,0,0,.78), transparent 92%)", pointerEvents: "none" }} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(380px, .82fr)" }, gap: { xs: 4, md: 7.5 }, alignItems: "center" }}>
          <Box sx={{ maxWidth: 700 }}>
            <Chip
              icon={<CheckCircleRoundedIcon />}
              label={isAuthenticated ? `Sesión activa${userName ? ` · ${userName}` : ""}` : "WebProfit · tu negocio en un solo lugar"}
              sx={{ mb: { xs: 1.8, sm: 2.3 }, minHeight: 34, borderRadius: 999, bgcolor: "rgba(243,90,58,.11)", color: BRAND.orangeSoft, border: "1px solid rgba(243,90,58,.16)", fontWeight: 900, "& .MuiChip-icon": { color: BRAND.orange } }}
            />

            <Typography id="webprofit-home-title" component="h1" sx={{ color: "#F8FAFC", fontWeight: 950, letterSpacing: "-.058em", lineHeight: .98, fontSize: { xs: "2.65rem", sm: "3.8rem", md: "4.65rem", lg: "5.1rem" } }}>
              Tu negocio,
              <Box component="span" sx={{ display: "block", color: BRAND.orange }}>más claro.</Box>
            </Typography>

            <Typography sx={{ mt: { xs: 1.6, sm: 2.1 }, maxWidth: 580, color: "rgba(247,248,250,.62)", fontSize: { xs: ".96rem", sm: "1.06rem" }, lineHeight: 1.55, fontWeight: 600 }}>
              Web, ventas y gestión conectadas. Empezá por lo que hoy más te frena.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1} sx={{ mt: { xs: 2.6, sm: 3.2 }, maxWidth: 610 }}>
              <Grow in timeout={420}>
                <Button onClick={onExploreClick} variant="contained" size="large" startIcon={<AutoAwesomeRoundedIcon />} endIcon={<ArrowForwardRoundedIcon />} sx={{ minHeight: 58, px: 3.1, borderRadius: 999, color: "#fff", bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, boxShadow: "0 14px 34px rgba(243,90,58,.25)", transition: "transform 260ms cubic-bezier(.18,.9,.24,1.35), box-shadow 260ms ease, background-color 180ms ease", "&:hover": { bgcolor: "#E64A2A", transform: "translateY(-3px) scale(1.015)", boxShadow: "0 20px 42px rgba(243,90,58,.30)" }, "&:active": { transform: "translateY(0) scale(.975)" }, "& .MuiButton-startIcon": { transition: "transform 260ms cubic-bezier(.18,.9,.24,1.35)" }, "&:hover .MuiButton-startIcon": { transform: "rotate(-8deg) scale(1.15)" }, "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } } }}>
                  ¿Qué es WebProfit?
                </Button>
              </Grow>
              <Grow in timeout={560}>
                <Button onClick={onStartClick} variant="outlined" size="large" endIcon={<ArrowForwardRoundedIcon />} sx={{ minHeight: 58, px: 2.7, borderRadius: 999, color: "#F8FAFC", borderColor: "rgba(255,255,255,.19)", bgcolor: "rgba(255,255,255,.025)", textTransform: "none", fontWeight: 900, transition: "transform 240ms cubic-bezier(.18,.9,.24,1.25), border-color 180ms ease, background-color 180ms ease", "&:hover": { borderColor: "rgba(255,255,255,.42)", bgcolor: "rgba(255,255,255,.06)", transform: "translateY(-2px)" }, "&:active": { transform: "scale(.98)" }, "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } } }}>
                  {primaryLabel}
                </Button>
              </Grow>
            </Stack>

            <RealStats stats={stats} />
          </Box>

          <Paper elevation={0} sx={{ position: "relative", overflow: "hidden", p: { xs: 1.5, sm: 2 }, borderRadius: { xs: 4, sm: 5 }, bgcolor: "rgba(17,24,32,.88)", color: "#fff", border: "1px solid rgba(255,255,255,.09)", boxShadow: "0 34px 90px rgba(0,0,0,.38)", backdropFilter: "blur(20px)" }}>
            <Box sx={{ px: { xs: .4, sm: .8 }, pt: .5, pb: 1.5 }}>
              <Typography sx={{ color: BRAND.orangeSoft, fontWeight: 950, letterSpacing: ".09em", textTransform: "uppercase", fontSize: ".7rem" }}>Entrada rápida</Typography>
              <Typography sx={{ mt: .55, fontWeight: 950, letterSpacing: "-.03em", fontSize: { xs: "1.4rem", sm: "1.75rem" } }}>¿Qué querés ordenar primero?</Typography>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: .9 }}>
              {QUICK_NEEDS.map(({ key, label, helper, icon: Icon }, index) => (
                <Grow key={key} in timeout={360 + index * 70}>
                  <Button
                    onClick={() => onQuickNeed?.(key)}
                    variant="outlined"
                    sx={{
                      justifyContent: "flex-start",
                      alignItems: "center",
                      minHeight: { xs: 74, sm: 80 },
                      px: { xs: 1.25, sm: 1.45 },
                      borderRadius: 3.2,
                      textTransform: "none",
                      color: "rgba(255,255,255,.88)",
                      borderColor: "rgba(255,255,255,.08)",
                      bgcolor: "rgba(255,255,255,.035)",
                      gap: 1,
                      transition: "transform 260ms cubic-bezier(.18,.9,.24,1.35), border-color 180ms ease, background-color 180ms ease",
                      "&:hover": { bgcolor: "rgba(243,90,58,.105)", borderColor: "rgba(243,90,58,.34)", color: "#fff", transform: "translateY(-3px) scale(1.012)" },
                      "&:active": { transform: "translateY(0) scale(.975)" },
                      "&:hover .quick-need-icon": { transform: "rotate(-6deg) scale(1.12)" },
                      "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } },
                    }}
                  >
                    <Box className="quick-need-icon" sx={{ width: 38, height: 38, borderRadius: 2.35, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: BRAND.orangeSoft, flexShrink: 0, transition: "transform 260ms cubic-bezier(.18,.9,.24,1.35)" }}><Icon sx={{ fontSize: 20 }} /></Box>
                    <Box sx={{ minWidth: 0, textAlign: "left" }}>
                      <Typography sx={{ fontWeight: 950, fontSize: { xs: ".82rem", sm: ".9rem" }, lineHeight: 1.15 }}>{label}</Typography>
                      <Typography sx={{ mt: .28, color: "rgba(255,255,255,.42)", fontWeight: 700, fontSize: { xs: ".64rem", sm: ".68rem" }, lineHeight: 1.2 }}>{helper}</Typography>
                    </Box>
                  </Button>
                </Grow>
              ))}
            </Box>

            <Typography sx={{ mt: 1.45, px: .8, color: "rgba(255,255,255,.42)", fontSize: ".73rem", fontWeight: 700 }}>
              Tocá lo que hoy más te importa. Lo guardamos como tu prioridad y el diagnóstico continúa desde ahí.
            </Typography>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
}
